import { Input, Radio, Select, Spin, Alert } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { FaTrash } from "react-icons/fa6";
import trans from "../../../../utils";
import {
	useGetAllEntitiesQuery,
	useGetEntityMetadataQuery,
	useGetValidParentsQuery,
} from "../../../../Redux/service/Forms/FormEntities";

export default function FormOptionDynamicDropdown({
	placeholder,
	dropdownOptions,
	id,
	onChange,
	queryKey,
	addForm,
	localErrors,
	setLocalErrors,
	siblingFields,
}) {
	const defaultStaticOptions = [
		{ label: `${trans("Option")} 1`, value: "Option 1" },
		{ label: `${trans("Option")} 2`, value: "Option 2" },
		{ label: `${trans("Option")} 3`, value: "Option 3" },
	];

	const [savedStaticOptions, setSavedStaticOptions] = useState([]);
	const [options, setOptions] = useState([]);
	const [dbKey, setDbKey] = useState("");

	// Dynamic dropdown state
	const [selectedEntity, setSelectedEntity] = useState(null);
	const [selectedDisplayField, setSelectedDisplayField] = useState(null);
	const [selectedParentEntityKey, setSelectedParentEntityKey] = useState(null);

	// Track mount state to prevent initial onChange loop
	const isMounted = useRef(false);

	// API hooks - only fetch when in dynamic mode
	const { data: entitiesData, isLoading: entitiesLoading } = useGetAllEntitiesQuery(undefined, {
		skip: queryKey !== "dynamic",
	});
	const { data: entityMetadata, isLoading: metadataLoading } = useGetEntityMetadataQuery(
		selectedEntity,
		{ skip: !selectedEntity || queryKey !== "dynamic" }
	);
	// Fetch valid parents for the selected entity
	const { data: validParentsData } = useGetValidParentsQuery(
		selectedEntity,
		{ skip: !selectedEntity || queryKey !== "dynamic" }
	);

	// Initialize from props
	useEffect(() => {
		if (queryKey === "static") {
			const fallback =
				savedStaticOptions.length > 0
					? savedStaticOptions
					: dropdownOptions?.data?.length > 0
					? dropdownOptions.data
					: defaultStaticOptions;
			setOptions(fallback);
		} else if (queryKey === "dbkey") {
			setDbKey(dropdownOptions?.queryKey || "");
		} else if (queryKey === "dynamic") {
			if (dropdownOptions?.entityConfig) {
				setSelectedEntity(dropdownOptions.entityConfig.entityKey);
				setSelectedDisplayField(dropdownOptions.entityConfig.displayField);
				if (dropdownOptions.entityConfig.dependsOn) {
					setSelectedParentEntityKey(dropdownOptions.entityConfig.dependsOn.parentEntityKey);
				}
			}
		}
	}, [id, queryKey]);

	// Get other dynamic dropdowns/multi-selects in the same scope that could be parents
	const scopeFields = siblingFields || addForm?.values?.formSchema?.fields || [];
	const otherDynamicDropdowns = scopeFields.filter(
		(field) =>
			(field.name === "Dropdown" || field.name === "MultiSelect") &&
			field.key !== id &&
			field.dropdownOptions?.queryKey === "dynamic" &&
			field.dropdownOptions?.entityConfig?.entityKey
	);

	// Filter: only show parent dropdowns whose entity is in validParentsData
	const validParents = validParentsData?.data || [];
	const allowedParentDropdowns = otherDynamicDropdowns.filter((field) => {
		const fieldEntityKey = field.dropdownOptions?.entityConfig?.entityKey;
		return validParents.some((vp) => vp.parentEntityKey === fieldEntityKey);
	});

	// Report value up to parent
	useEffect(() => {
		if (!onChange) return;

		// Skip the first render to prevent infinite loop
		if (!isMounted.current) {
			isMounted.current = true;
			return;
		}

		if (queryKey === "static") {
			onChange({
				target: {
					value: {
						queryKey: "static",
						data: options,
					},
				},
			});
		} else if (queryKey === "dbkey") {
			onChange({
				target: {
					value: {
						queryKey: dbKey,
						data: null,
					},
				},
			});
		} else if (queryKey === "dynamic") {
			const entityConfig = {
				entityKey: selectedEntity,
				displayField: selectedDisplayField,
				valueField: "id",
			};

			// Add dependency config if parent entity is selected
			if (selectedParentEntityKey) {
				// Find the relation field from validParents
				const parentRelation = validParents.find(
					(vp) => vp.parentEntityKey === selectedParentEntityKey
				);
				// Find the parent dropdown field key
				const parentDropdown = otherDynamicDropdowns.find(
					(field) => field.dropdownOptions?.entityConfig?.entityKey === selectedParentEntityKey
				);

				if (parentRelation && parentDropdown) {
					entityConfig.dependsOn = {
						parentEntityKey: selectedParentEntityKey,
						parentFieldKey: parentDropdown.key,
						relationField: parentRelation.relationField,
					};
				}
			}

			onChange({
				target: {
					value: {
						queryKey: "dynamic",
						entityConfig,
					},
				},
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options, dbKey, queryKey, selectedEntity, selectedDisplayField, selectedParentEntityKey]);

	// Validation for static options
	useEffect(() => {
		if (queryKey === "static") {
			const newErrors = {};
			options.forEach((opt, i) => {
				if (!opt.label || opt.label.trim() === "") {
					newErrors[`${id}_${i}`] = trans("This field is required");
				}
			});

			setLocalErrors((prev) => {
				const updated = { ...prev, ...newErrors };
				Object.keys(updated).forEach((key) => {
					if (key.startsWith(`${id}_`) && !(key in newErrors)) {
						delete updated[key];
					}
				});
				return updated;
			});
		}
	}, [options, queryKey, setLocalErrors, id]);

	// Handlers for static options
	const handleLabelOptionChange = (index, newValue) => {
		setOptions((prev) =>
			prev.map((option, i) =>
				i === index ? { ...option, label: newValue, value: newValue } : option
			)
		);
	};

	const addOption = () => {
		setOptions((prev) => [
			...prev,
			{ label: `${trans("Option")} ${prev.length + 1}`, value: "" },
		]);
	};

	const handleQueryKeyChange = (e) => {
		const newQueryKey = e.target.value;

		if (queryKey === "static") {
			setSavedStaticOptions(options);
		}

		onChange({
			target: {
				value: {
					queryKey: newQueryKey,
				},
			},
		});
	};

	return (
		<div className="field-settings-section">
			<h5 className="field-settings-section-title">{trans("Dropdown Options")}</h5>
			<Radio.Group
				style={{ display: "flex", flexDirection: "column", gap: 8 }}
				onChange={handleQueryKeyChange}
				value={queryKey}
				options={[
					{ value: "static", label: trans("Custom data") },
					{ value: "dynamic", label: trans("Dynamic (Entity-based)") },
				]}
			/>

			{queryKey === "static" && (
				<div className="position-relative mt-2">
					<div className="task-radio-layout" key={id}>
						{options.map((option, index) => (
							<div key={index}>
								<div className="task-option-item mb-2 d-flex">
									<label htmlFor={`option-${index}`} className="form-label">
										{`${trans("Option")} ${index + 1}`}
									</label>
									<FaTrash
										color="red"
										className="task-option-bin"
										onClick={() => {
											const newOptions = [...options];
											newOptions.splice(index, 1);
											if (newOptions.length === 0) {
												newOptions.push(
													{ value: "Option 1", label: `${trans("Option")} 1` },
													{ value: "Option 2", label: `${trans("Option")} 2` },
													{ value: "Option 3", label: `${trans("Option")} 3` }
												);
											}
											setOptions(newOptions);
										}}
									/>
								</div>
								<div className="ms-3 mb-2">
									<div className="flex-between mb-2">
										<label className="form-label">{trans("Label")}</label>
										<Input
											placeholder={placeholder}
											value={option.label}
											onChange={(e) =>
												handleLabelOptionChange(index, e.target.value)
											}
										/>
										{localErrors[`${id}_${index}`] && (
											<div
												className="text-danger"
												style={{ marginTop: 4, fontSize: 12 }}
											>
												{localErrors[`${id}_${index}`]}
											</div>
										)}
									</div>
								</div>
							</div>
						))}
					</div>
					<div
						onClick={addOption}
						className="cursor-pointer text-blue-500 hover:text-blue-700 task-radio-add-btn"
					>
						{trans("Add")}
					</div>
				</div>
			)}

			{queryKey === "dynamic" && (
				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					{/* Entity Selection */}
					<div>
						<label className="form-label d-flex align-items-center">
							{trans("Select Entity")} *
						</label>
						{entitiesLoading ? (
							<Spin size="small" />
						) : (
							<Select
								style={{ width: "100%" }}
								placeholder={trans("Choose an entity")}
								value={selectedEntity}
								onChange={(value) => {
									setSelectedEntity(value);
									setSelectedDisplayField(null);
									setSelectedParentEntityKey(null);
								}}
								options={entitiesData?.data?.map((entity) => ({
									label: entity.displayName,
									value: entity.entityKey,
								}))}
							/>
						)}
					</div>

					{/* Display Field Selection */}
					{selectedEntity && (
						<div>
							<label className="form-label d-flex align-items-center">
								{trans("Display Field")} *
							</label>
							{metadataLoading ? (
								<Spin size="small" />
							) : (
								<Select
									style={{ width: "100%" }}
									placeholder={trans("Choose field to display")}
									value={selectedDisplayField}
									onChange={setSelectedDisplayField}
									options={entityMetadata?.data?.availableFields?.map((field) => ({
										label: field.label,
										value: field.key,
									}))}
								/>
							)}
						</div>
					)}

					{/* Dependency Configuration - Only show if valid parents exist */}
					{selectedEntity && selectedDisplayField && allowedParentDropdowns.length > 0 && (
						<div>
							<label className="form-label d-flex align-items-center">
								{trans("Depends on")} ({trans("Optional")})
							</label>
							<Select
								style={{ width: "100%" }}
								placeholder={trans("Select parent dropdown (optional)")}
								value={selectedParentEntityKey}
								allowClear
								onChange={(value) => {
									setSelectedParentEntityKey(value || null);
								}}
								options={allowedParentDropdowns.map((field) => {
									const fieldEntityKey = field.dropdownOptions?.entityConfig?.entityKey;
									const parentInfo = validParents.find(
										(vp) => vp.parentEntityKey === fieldEntityKey
									);
									return {
										label: `${field.title || field.label} (${parentInfo?.label || fieldEntityKey})`,
										value: fieldEntityKey,
									};
								})}
							/>
						<p className="field-settings-help">
							{trans("This dropdown will filter based on the selected parent value")}
						</p>
						</div>
					)}

					{/* Show message if entity has NO valid parents and user might expect it */}
					{selectedEntity && selectedDisplayField && validParents.length === 0 && otherDynamicDropdowns.length > 0 && (
						<p className="field-settings-help">
							{trans("This entity has no dependency relationships with other entities in this form")}
						</p>
					)}

					{/* Validation Message */}
					{(!selectedEntity || !selectedDisplayField) && (
						<Alert
							message={trans("Please select both entity and display field")}
							type="warning"
							showIcon
						/>
					)}
				</div>
			)}

			{queryKey === "dbkey" && (
				<>
					<label
						htmlFor={id}
						className="form-label d-flex align-items-center mt-2"
					>
						{trans("Database key")}
					</label>
					<Input
						type="text"
						value={dbKey}
						placeholder={trans("Enter Query key")}
						onChange={(e) => setDbKey(e.target.value)}
					/>
				</>
			)}
		</div>
	);
}
